import { logger } from './logger';
import api from './api';
import axios from 'axios';
import { resolveFileMimeType } from './uploadUtils';
export interface UploadSession {
  uploadId: string;
  catalogId: string;
  imageId: string;
  bucket: string;
  key: string;
  presignedUrls?: Array<{
    partNumber: number;
    url: string;
  }>;
  aliCatalogNumber?: string;
}

export interface UploadPart {
  ETag: string;
  PartNumber: number;
}

export const initiateMultipartUpload = async (
  file: File,
  catalogId?: string,
  imageId?: string,
  ownerId?: string | null,
  catalogAliId?: string | null
): Promise<UploadSession> => {
  const startTime = Date.now();

  try {
    // Build request body - only include optional fields if provided
    const requestBody: {
      filename: string;
      fileSize: number;
      mimeType: string;
      catalogId?: string;
      imageId?: string;
      partCount?: number;
    } = {
      filename: file.name,
      fileSize: file.size,
      mimeType: resolveFileMimeType(file),
    };

    if (catalogId) {
      requestBody.catalogId = catalogId;
    }

    if (imageId) {
      requestBody.imageId = imageId;
    }

    // Declare how many parts we will actually upload (#1250), computed from
    // the SAME chunk-size function the upload loop uses to slice the file
    // (calculateOptimalChunkSize) so the count the server mints presigned
    // URLs for always matches what useMultipartUpload will request. Without
    // this, files needing more than the server's fixed default part count
    // (10) die mid-upload with "No presigned URL found for part N".
    requestBody.partCount = Math.ceil(file.size / calculateOptimalChunkSize(file.size));

    const query = new URLSearchParams();
    if (ownerId) query.set('owner_id', ownerId);
    if (catalogAliId) query.set('catalogAliId', catalogAliId);
    const queryString = query.toString();
    const url = queryString ? `/api/upload/initiate?${queryString}` : '/api/upload/initiate';

    const response = await api.post(url, requestBody);

    return response.data;
  } catch (error) {
    logger.error('Upload initiation failed', {
      filename: file.name,
      error: {
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      },
      duration: Date.now() - startTime,
      timestamp: new Date().toISOString()
    });

    if (error.response?.status === 401) {
      throw new Error('Authentication required. Please log in again.');
    }
    throw error;
  }
};

export const uploadChunk = async (
  chunk: Blob,
  uploadUrl: string,
  onProgress: (progress: number) => void,
  maxRetries: number = 3
): Promise<string> => {
  const startTime = Date.now();

  // Extract part number and upload ID from URL for logging
  const urlParams = new URLSearchParams(uploadUrl.split('?')[1]);
  const partNumber = urlParams.get('partNumber');
  const uploadId = urlParams.get('uploadId');

  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Use plain axios for S3 presigned URL uploads (no JWT token needed)
      // Don't set any custom headers - let S3 handle the signature validation
      const response = await axios.put(uploadUrl, chunk, {
        headers: {},
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = (progressEvent.loaded / progressEvent.total) * 100;
            onProgress(progress);
          }
        },
        timeout: 30000, // 30 second timeout for chunk uploads
      });

      return response.headers.etag;
    } catch (error: unknown) {
      lastError = error as Error;

      // Log detailed error information
      logger.error('Upload Part Error', {
        partNumber,
        uploadId: uploadId?.substring(0, 20) + '...',
        error: {
          status: error.response?.status,
          statusText: error.response?.statusText,
          message: error.message,
          code: error.code,
          responseHeaders: error.response?.headers ? Object.fromEntries(
            Object.entries(error.response.headers).filter(([key]) =>
              key.toLowerCase().includes('amz') || key.toLowerCase().includes('error')
            )
          ) : undefined
        },
        attempt,
        maxRetries,
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      });

      // Don't retry on certain error types
      if (error.response?.status === 403 || error.response?.status === 404) {
        break;
      }

      // If this isn't the last attempt, wait before retrying
      if (attempt < maxRetries) {
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000); // Exponential backoff, max 5s
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  // All retries failed
  if (lastError?.response?.status === 403) {
    throw new Error('Upload session expired or invalid. Please retry the upload.');
  }

  throw new Error(`Upload failed: ${lastError?.message || lastError}`);
};

export const completeMultipartUpload = async (
  uploadId: string,
  catalogId: string,
  imageId: string,
  parts: UploadPart[],
  ownerId?: string | null
): Promise<void> => {
  const startTime = Date.now();

  try {
    const params = ownerId ? `?owner_id=${encodeURIComponent(ownerId)}` : '';
    await api.post(`/api/upload/complete/${catalogId}/${imageId}${params}`, {
      uploadId,
      parts,
    });
  } catch (error) {
    logger.error('Multipart upload completion failed', {
      uploadId: uploadId.substring(0, 20) + '...',
      catalogId,
      imageId,
      partsCount: parts?.length || 0,
      error: {
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      },
      duration: Date.now() - startTime,
      timestamp: new Date().toISOString()
    });
    throw error;
  }
};

export const calculateOptimalChunkSize = (fileSize: number): number => {
  const MB = 1024 * 1024;
  const GB = 1024 * MB;

  if (fileSize < 100 * MB) {
    return 8 * MB; // 8MB for files < 100MB
  }
  if (fileSize < GB) {
    return 16 * MB; // 16MB for files < 1GB
  }
  return 64 * MB; // 64MB for larger files
};

// Processing status integration functions
export const getProcessingStatusFromUpload = async (catalogId: string, ownerId?: string | null) => {
  const params = ownerId ? `?owner_id=${encodeURIComponent(ownerId)}` : '';
  const response = await api.get(`/api/processing/status/${catalogId}${params}`);
  return response.data;
};

export const retryProcessingFromUpload = async (catalogId: string) => {
  await api.post(`/api/processing/retry/${catalogId}`);
};

export const getThumbnailUrl = async (catalogId: string): Promise<string | undefined> => {
  try {
    const response = await api.get(`/api/processing/thumbnail/${catalogId}`);
    return response.data.thumbnailUrl;
  } catch (error) {
    logger.debug('Failed to fetch thumbnail URL', error);
    return undefined;
  }
};
